import { createStyles, lighten, makeStyles, Theme } from '@material-ui/core';
import { DatePicker } from '@talentnow/shared-ui/form-components';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';

import TextField from '../../../../_components/forms/TextField';
import TnButton from '../../../../_components/tn-button/TnButton';
import TnTextEditor from '../../../../_components/tn-text-editor/TnTextEditor';
import TnTypography from '../../../../_components/tn-typography/TnTypography';

const REQUIRED_FIELD_MESSAGE = 'This field is required';
const END_DATE_BEFORE_START_MESSAGE = 'End date cannot be before start date';

const FIELD_LABELS: Record<string, string> = {
  schoolName: 'School Name',
  degreeName: 'Degree Name',
  startDate: 'Education Start Date',
  endDate: 'Education End Date',
};

export interface EducationEntity {
  id?: string;
  schoolName: string;
  degreeName: string;
  startDate: string;
  endDate: string;
  gpa: number | null;
  additionalInfo: string;
}

interface Props {
  mode: 'create' | 'edit';
  onCancel: () => void;
  onDelete?: () => void;
  onSubmit: (education: EducationEntity) => void;
  education?: EducationEntity;
  onValidityChange?: (isValid: boolean, invalidFields: string[]) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bodyContainer: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
    },
    bodyElement: {
      width: 'calc(50% - 8px)',
    },
    fullWidthElement: {
      width: '100%',
    },
    additionalInfoContainer: {
      marginTop: -16,
      width: '100%',
    },
    buttonRow: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      marginBottom: 16,
    },
    createModeButtonContainer: {
      display: 'flex',
      gap: 8,
      flexDirection: 'row',
    },
    deleteButton: {
      backgroundColor: '#B00020',
      color: 'white',
      padding: '10px 20px',
      border: 'solid',
      borderRadius: '6px',
      '&:hover': {
        backgroundColor: lighten('#B00020', 0.1),
      },
    },
  }),
);

export default function EducationForm({
  mode,
  onSubmit,
  onCancel,
  onDelete,
  education,
  onValidityChange,
}: Readonly<Props>) {
  const classes = useStyles();

  const [schoolName, setSchoolName] = React.useState('');
  const [degreeName, setDegreeName] = React.useState('');
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [gpa, setGpa] = React.useState<string | null>(null);
  const [additionalInfo, setAdditionalInfo] = React.useState('');
  const [touchedFields, setTouchedFields] = React.useState<Record<string, boolean>>({});

  useEffect(() => {
    if (education && mode === 'edit') {
      setSchoolName(education.schoolName);
      setDegreeName(education.degreeName);
      setStartDate(education.startDate);
      setEndDate(education.endDate);
      setGpa(education.gpa?.toString() || null);
      setAdditionalInfo(education.additionalInfo);
      setTouchedFields({ schoolName: true, degreeName: true, startDate: true, endDate: true });
    }
  }, [education, mode]);

  const parsedStartDate = useMemo(() => moment(startDate), [startDate]);
  const parsedEndDate = useMemo(() => moment(endDate), [endDate]);

  const markFieldTouched = (field: string) => setTouchedFields((previous) => ({ ...previous, [field]: true }));

  const errors = useMemo(() => {
    const fieldErrors: Record<string, string> = {};

    if (!schoolName) {
      fieldErrors.schoolName = REQUIRED_FIELD_MESSAGE;
    }

    if (!degreeName) {
      fieldErrors.degreeName = REQUIRED_FIELD_MESSAGE;
    }

    if (startDate && !parsedStartDate.isValid()) {
      fieldErrors.startDate = 'Invalid Date Format';
    }

    if (endDate && !parsedEndDate.isValid()) {
      fieldErrors.endDate = 'Invalid Date Format';
    } else if (startDate && endDate && parsedStartDate.isValid() && parsedEndDate.isBefore(parsedStartDate)) {
      fieldErrors.endDate = END_DATE_BEFORE_START_MESSAGE;
    }

    return fieldErrors;
  }, [schoolName, degreeName, startDate, endDate, parsedStartDate, parsedEndDate]);

  const isFormValid = useMemo(() => Object.keys(errors).length === 0, [errors]);
  const invalidFields = useMemo(() => Object.keys(errors).map((field) => FIELD_LABELS[field]), [errors]);

  useEffect(() => {
    onValidityChange?.(isFormValid, invalidFields);
  }, [isFormValid, invalidFields]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={classes.bodyContainer}>
      <div className={classes.bodyElement}>
        <TextField
          required
          name="schoolName"
          label="School Name"
          value={schoolName}
          error={errors.schoolName}
          touched={touchedFields.schoolName}
          onChange={(value) => setSchoolName(value)}
          onBlur={() => markFieldTouched('schoolName')}
        />
      </div>
      <div className={classes.bodyElement}>
        <TextField
          required
          name="degreeName"
          label="Degree Name"
          value={degreeName}
          error={errors.degreeName}
          touched={touchedFields.degreeName}
          onChange={(value) => setDegreeName(value)}
          onBlur={() => markFieldTouched('degreeName')}
        />
      </div>
      <div className={classes.bodyElement}>
        <DatePicker
          monthYearOnly
          name="startDate"
          placeholder="MM/YYYY"
          label="Start Date"
          value={startDate}
          minDate={'01/01/1900'}
          error={errors.startDate}
          touched={!!touchedFields.startDate}
          onChange={(value) => {
            markFieldTouched('startDate');
            setStartDate(value);
          }}
          onBlur={() => markFieldTouched('startDate')}
        />
      </div>
      <div className={classes.bodyElement}>
        <DatePicker
          monthYearOnly
          name="endDate"
          label="End Date"
          placeholder="MM/YYYY"
          value={endDate}
          minDate={'01/01/1900'}
          error={errors.endDate}
          touched={!!touchedFields.endDate}
          onChange={(value) => {
            markFieldTouched('endDate');
            setEndDate(value);
          }}
          onBlur={() => markFieldTouched('endDate')}
        />
      </div>
      <div className={classes.bodyElement}>
        <TextField type="float" name="gpa" label="GPA" value={gpa || ''} onChange={(value) => setGpa(value)} />
      </div>
      <div className={classes.additionalInfoContainer}>
        <TnTypography variant="body2">Additional Information</TnTypography>
        <TnTextEditor value={additionalInfo} onTextChange={(value) => setAdditionalInfo(value)} />
      </div>
      <div className={classes.buttonRow}>
        <div>
          {mode === 'edit' ? (
            <TnButton
              variant="contained"
              color="secondary"
              classes={{ root: classes.deleteButton }}
              value="Delete"
              onClick={() => onDelete?.()}
            />
          ) : null}
        </div>
        <div className={classes.createModeButtonContainer}>
          <TnButton variant="outlined" color="primary" value="Cancel" onClick={() => onCancel()} />
          <TnButton
            variant="contained"
            color="primary"
            value={mode === 'create' ? 'Add Education' : 'Save Changes'}
            disabled={!isFormValid}
            onClick={() => {
              onSubmit({
                id: education?.id,
                schoolName,
                degreeName,
                startDate,
                endDate,
                gpa: gpa ? parseFloat(gpa) : null,
                additionalInfo,
              });
            }}
          />
        </div>
      </div>
    </div>
  );
}
